import { property, subclass } from '@arcgis/core/core/accessorSupport/decorators';
import { messageBundle, storeNode, tsx } from '@arcgis/core/widgets/support/widget';
import Widget from '@arcgis/core/widgets/Widget';
import { VNode } from 'src/interfaces/interfaces';
import PublishAppViewModel from '../data/PublishApp/PublishAppViewModel';
import { keyboardAccessibleHelper } from '../utils/appUtils';

import { EAppTypeIdentifier, EWebmapNotCompatible } from '../utils/Compatibility/webmapChecks';
import AppTypeInfoModal from './AppTypeInfoModal';
import AppTypeTileViewModel from './AppTypeTile/AppTypeTileViewModel';

import "./AppTypeTile/styles/AppTypeTile.scss";
import IFrameAppPreview from './IFrameAppPreview';
import PublishForm from './PublishForm';

import CircleDownCarotSVG from "../../../public/assets/images/Info_icon.svg";
import { applyShadowStyle, applyTextFade } from '../utils/utils';
import InfoBadge from './InfoBadge';
import Collection from '@arcgis/core/core/Collection';
import { ddButtonDef } from './AppTile';
import { focusElement } from '../utils/focusUtils';
import { SHADOW_STYLE_DROPDOWN } from '../utils/shadowStyles';

const INTRINSIC_IMG_SIZE = 1000;

export interface AppTypeInfo {
  thumbnailUrl: string;
  screenshotUrl: string;
  created?: Date;
  badge?: string;
  appTypeTitle: string;
  appTypeUrlFragment: EAppTypeIdentifier; // end url (/apps/instant/3dviewer/index.html)
  appTypeUrl: string; // full url for portal (ex: https://devext.arcgis.com/apps/instant/3dviewer/index.html)
  compatibleMsgs: EWebmapNotCompatible[];
  appTypeId: string; // Id of the App Type's PortalItem (diff between diff envs (devext, prod... etc))
  portalItem: __esri.PortalItem
}

export interface AppTypeTileProperties extends __esri.WidgetProperties {
  appTypeInfo: AppTypeInfo;
  publishAppVM: PublishAppViewModel;
  webmapItem: __esri.PortalItem;
}

export type previewType = "samplePreview" | "mapPreview";

const SAMPLE_PREVIEWS = new Collection([
  EWebmapNotCompatible.CatGalRequirement, 
  EWebmapNotCompatible.ThreeDRequirement
]);


@subclass('Landing.widgets.AppTypeTile')
export default class AppTypeTile extends Widget {
  @property()
  identifier: "AppTypeTile" = "AppTypeTile";

  @property()
  appTypeInfo: AppTypeInfo;

  @property()
  publishAppVM: PublishAppViewModel;

  @property()
  webmapItem: __esri.PortalItem;

  @property()
  publishModal: PublishForm;

  @property()
  infoModal: AppTypeInfoModal;

  @property()
  iFramePreviewModal: IFrameAppPreview;

  @property()
  previewButtonDOM: HTMLCalciteButtonElement;

  @property()
  @messageBundle("dist/assets/t9n/widgets/AppTypeTile/AppTypeTile")
  messages;

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages;

  @property()
  viewModel = new AppTypeTileViewModel();

  @property()
  infoBadge: InfoBadge;

  private _previewType: previewType;
  private _hamburgerMenuButtons: ddButtonDef[];
  
  /** Saved active element when a modal is launched */
  private _activeElement: HTMLElement;

  constructor(properties?: AppTypeTileProperties) {
    super(properties);
  }

  postInitialize() {
    const { compatibleMsgs, badge, thumbnailUrl } = this.appTypeInfo;
    this._previewType = this._decidePreviewBtnTitle(compatibleMsgs);

    this.appTypeInfo.thumbnailUrl = this._decideThumbnailUrl(thumbnailUrl);
    if(badge && !this.publishAppVM.portal.isPortal){
      this.infoBadge = new InfoBadge({badge});
    }

    this._initHamburgerMenu();
  }

  render() {
    const tileImg = this._renderTileImg();
    const titleArea = this._renderTitleArea();
    const buttonArea = this._renderButtonArea();
    const { compatibleMsgs } = this?.appTypeInfo;
    return (
      <div class="app-type-tile">
        { tileImg}
        { titleArea}
        { buttonArea}
        { compatibleMsgs != null && compatibleMsgs.length > 0 ? (
          <div id="notcompatMsgs" key="notcompatMsgs">
            <calcite-icon
              id="warning-icon"
              icon="exclamation-mark-triangle-f"
              scale="s">
            </calcite-icon>
            {compatibleMsgs.map((msg, index) => {
              return <div key={`notcompatMsg${index}`} class="warning-text">{this.appMessages.appRequirements[msg]}</div>
            })}
          </div>) : null}

        { this.publishModal?.render()}
        { this.infoModal?.render()}
        { this.iFramePreviewModal?.render()}

      </div>
    );
  }

  private _initHamburgerMenu(){
    const { viewItemDetails, appDescription } = this.appMessages.hamburgerBtns;
    this._hamburgerMenuButtons = [
      { icon: "infographic", label: viewItemDetails, onClickFunc: ()=>{
        window.open(`${this.publishAppVM.orgUrl}/home/item.html?id=${this.appTypeInfo.appTypeId}`);
      } },
      { icon: "information", label: appDescription, onClickFunc: ()=>{
        this._initializeInfoModal();
      } },
    ]
  }

  private _renderTitleArea() {
    const { appTypeTitle } = this?.appTypeInfo;
    return (
      <div class="app-type-tile__title">
        <div class="leftAlign">       
          {this._renderHamburgerMenu()}
          <div id="app-title">
            <div>
              <span afterCreate={applyTextFade} title={appTypeTitle}>
                {appTypeTitle}
              </span>
            </div>
          </div>
          <img
            src={CircleDownCarotSVG}
            tabindex="0"
            onclick={this._initializeInfoModal.bind(this)}
            onkeydown={keyboardAccessibleHelper.bind(
              this,
              this._initializeInfoModal.bind(this)
            )}
            class="down-chevron-svg"
            alt="" />
        </div>
        {this.infoBadge?.render()}
      </div>
    )
  }

  private _renderButtonArea() {
    const isSampleAndInPortal: boolean = this._checkIfSampleAndPortal();
    return (
      <div class="app-type-tile__buttons">
        { isSampleAndInPortal ? null : <calcite-button
          bind={this}
          afterCreate={storeNode}
          data-node-ref="previewButtonDOM"
          id="previewBtn"
          class="previewBtn"
          onclick={this._initializePreview.bind(this)}
          width="full"
          scale="s"
          icon-start="camera-flash-on"
          appearance="outline"
          title={this.appMessages.previewButtons[this._previewType]}>
          {this.messages.previewBtn}
        </calcite-button>}
        <calcite-button
          id="chooseBtn"
          class={this.classes("chooseBtn", isSampleAndInPortal && "chooseBtn--only")}
          onclick={this._initializePublish.bind(this)}
          width="full"
          scale="s"
        >
          {this.messages.chooseBtn}
        </calcite-button>
      </div>)
  }

  /** Checks if compat msg exists and the portal is disconnected. If true then the Preview button gets hidden */
  private _checkIfSampleAndPortal() : boolean{
    return this._previewType === "samplePreview" && this.publishAppVM.portal.isPortal;
  }

  private _renderTileImg(): VNode {
    const {
      thumbnailUrl,
      screenshotUrl
    } = this.appTypeInfo;

    return (
      <img class="app-type-tile__img"
        src={screenshotUrl != null ? screenshotUrl : thumbnailUrl}/>
    );
  }

  private _initializePublish() {
    this._saveActiveElem();
    this.publishModal = new PublishForm({
      ui: "modal",
      mode: "newApp",
      appTypeInfo: this.appTypeInfo,
      webmapItem: this.webmapItem,
      publishAppVM: this.publishAppVM,
      sendFocusOnClose: this._focusPriorActiveElem()
    });
  }

  private _initializeInfoModal() {
    this._saveActiveElem();
    const { thumbnailUrl, appTypeTitle, appTypeUrlFragment } = this.appTypeInfo;
    this.infoModal = new AppTypeInfoModal({
      picUrl: thumbnailUrl,
      appTypeTitle: appTypeTitle,
      appTypeUrlFragment: appTypeUrlFragment,
      sendFocusOnClose: this._focusPriorActiveElem()
    });
  }

  private _initializePreview(): void {
    this._saveActiveElem();
    this.iFramePreviewModal = new IFrameAppPreview({
      appTypeInfo: this.appTypeInfo,
      webmapItem: this.webmapItem,
      publishAppVM: this.publishAppVM,
      previewType: this._previewType,
      sendFocusOnClose: this._focusPriorActiveElem()
    });
  }

  private _renderHamburgerMenu(): VNode {
    return (
      <calcite-dropdown
        class="app-type-tile__dropdown-options"
        placement="bottom-leading"
        type="click"
        scale="m"
        width="m"
      >
        <calcite-button
          slot="dropdown-trigger"
          scale="s"
          appearance="transparent"
          color="blue"
          icon-start="handleVertical"
          alignment="center"
          width="auto"
        ></calcite-button>
        <calcite-dropdown-group
          role="menu"
          selection-mode="none"
          selectionMode="none"
        >
          {this._renderDropDownOptions()}
        </calcite-dropdown-group>
      </calcite-dropdown>
    );
  }

  private _renderDropDownOptions(): VNode[] {
    return (
      this?._hamburgerMenuButtons?.map((option: ddButtonDef) => {
        const { icon, label, onClickFunc } = option;
        return (
          <calcite-dropdown-item
            afterCreate={(ddItem) => applyShadowStyle(SHADOW_STYLE_DROPDOWN, ddItem)}
            title={label}
            icon-start={icon}
            class="appTypeTileDropDownLinks"
            onclick={(e) => {
              e.preventDefault();
              onClickFunc.call(this, e);
            }}
          >
            {label}
          </calcite-dropdown-item>
        )
      })
    )
  }

  private _decideThumbnailUrl(defaultUrl: string): string {
    return defaultUrl.indexOf("?") !== -1
      ? `${defaultUrl}&w=${INTRINSIC_IMG_SIZE}`
      : `${defaultUrl}?w=${INTRINSIC_IMG_SIZE}`;
  }

  private _decidePreviewBtnTitle(nonCompatMsgs: EWebmapNotCompatible[]): previewType {
    if(this.webmapItem == null){
      return "samplePreview";
    }

    const index = nonCompatMsgs.findIndex((msg: EWebmapNotCompatible) => {
      return SAMPLE_PREVIEWS.includes(msg);
    });
  
    return index === -1 ? "mapPreview" : "samplePreview";
  }

  private _saveActiveElem(){
    this._activeElement = document.activeElement as HTMLElement;
  }

  private _focusPriorActiveElem():Function{
    return focusElement.bind(this, this._activeElement);
  }

}
